import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Facebook, Heart, Instagram, Lock, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useSite } from "../lib/store";
import { Daisy, DaisyBadge } from "./Daisy";

const Newsletter = ({ t }) => {
  const [email, setEmail] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/newsletter", { email });
      toast.success("Děkujeme! Jste v obraze.");
      setEmail("");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <motion.section initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mx-auto max-w-7xl px-4 sm:px-8">
      <div className="relative flex flex-col items-center gap-6 overflow-hidden rounded-[2rem] bg-[#A3AC86] px-6 py-8 text-white md:flex-row md:px-12">
        <Heart className="hidden h-10 w-10 shrink-0 md:block" strokeWidth={1.4} />
        <div className="flex-1 text-center md:text-left">
          <h3 className="font-serif text-2xl font-extrabold italic sm:text-3xl">{t.newsletter_title}</h3>
          <p className="mt-1 max-w-sm text-sm text-white/90">{t.newsletter_text}</p>
        </div>
        <form onSubmit={submit} className="flex w-full max-w-md items-center gap-2 rounded-full bg-white p-1.5 pl-4">
          <Mail className="h-4 w-4 shrink-0 text-[#6E7565]" />
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Váš e-mail" className="min-w-0 flex-1 bg-transparent text-sm text-[#2D3426] outline-none" data-testid="newsletter-email-input" />
          <button className="btn-sage !px-5 !py-2.5" data-testid="newsletter-submit-btn">Přihlásit se</button>
        </form>
        <div className="animate-float hidden md:block"><Daisy size={70} /></div>
      </div>
    </motion.section>
  );
};

const Col = ({ title, links }) => (
  <div>
    <p className="mb-3 text-sm font-semibold">{title}</p>
    <ul className="space-y-1.5 text-sm text-[#6E7565]">
      {links.map(([to, label]) => (
        <li key={to + label}><Link to={to} className="transition-colors hover:text-[#7A8460]">{label}</Link></li>
      ))}
    </ul>
  </div>
);

export const Footer = () => {
  const { site } = useSite();
  const s = site?.settings || {};
  const t = s.texts || {};
  const cats = site?.categories || [];
  return (
    <footer className="mt-20 space-y-12" data-testid="site-footer">
      <Newsletter t={t} />
      <div className="mx-auto grid max-w-7xl grid-cols-2 gap-x-6 gap-y-10 px-4 sm:px-8 md:grid-cols-[1.4fr_1fr_1fr_1.3fr_1fr]">
        <div className="col-span-2 flex items-start gap-4 md:col-span-1">
          <DaisyBadge size={64} />
          <div>
            <p className="h-display text-2xl leading-[0.9]">hravá<br />mateřinka</p>
            <p className="mt-3 text-sm text-[#6E7565]">{t.footer_slogan}</p>
          </div>
        </div>
        <Col title="Rychlé odkazy" links={[["/", "Domů"], ["/eshop", "E-shop"], ["/darkove-poukazy", "Dárkové poukazy"], ["/o-nas", "O nás"], ["/kontakt", "Kontakt"]]} />
        <Col title="Kategorie" links={cats.map((c) => [`/kategorie/${c.slug}`, c.name])} />
        <Col title="Informace" links={[["/doprava-a-platba", "Doprava a platba"], ["/obchodni-podminky", "Obchodní podmínky"], ["/gdpr", "Ochrana osobních údajů (GDPR)"], ["/reklamace", "Reklamace a vrácení"], ["/gdpr", "Cookies"]]} />
        <div>
          <p className="mb-3 text-sm font-semibold">Sledujte nás</p>
          <div className="flex gap-3">
            <a href={s.social?.instagram || "#"} target="_blank" rel="noreferrer" className="grid h-10 w-10 place-items-center rounded-full bg-white transition-transform hover:-translate-y-1" data-testid="footer-instagram"><Instagram className="h-5 w-5" /></a>
            <a href={s.social?.facebook || "#"} target="_blank" rel="noreferrer" className="grid h-10 w-10 place-items-center rounded-full bg-white transition-transform hover:-translate-y-1" data-testid="footer-facebook"><Facebook className="h-5 w-5" /></a>
          </div>
          <p className="mt-6 flex -rotate-6 items-start gap-2 font-script text-xl leading-tight text-[#7A8460]">
            <Heart className="mt-1 h-4 w-4 shrink-0" /> {t.footer_note}
          </p>
        </div>
      </div>
      <div className="border-t">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-4 py-5 text-xs text-[#6E7565] sm:px-8 md:flex-row">
          <p>© {new Date().getFullYear()} {s.company?.name || "Hravá mateřinka"}. Všechna práva vyhrazena.</p>
          <p className="flex items-center gap-4">
            <span className="flex items-center gap-1"><Lock className="h-3.5 w-3.5" /> Bezpečné nakupování</span>
            <span className="rounded bg-[#1a1f71] px-1.5 py-0.5 text-[10px] font-black italic text-white">VISA</span>
            <span className="flex"><span className="h-4 w-4 rounded-full bg-[#eb001b]" /><span className="-ml-1.5 h-4 w-4 rounded-full bg-[#f79e1b]/90" /></span>
            <span className="rounded bg-[#2D3426] px-1.5 py-0.5 text-[10px] font-bold text-white">QR</span>
          </p>
          <p className="flex items-center gap-4">
            <span>Vytvořil <a href="https://www.4uweb.cz" target="_blank" rel="noreferrer" className="font-semibold text-[#7A8460] hover:underline" data-testid="footer-4uweb-link">4uweb – www.4uweb.cz</a></span>
            <Link to="/admin" className="hover:text-[#7A8460]" data-testid="footer-admin-link">Administrace</Link>
          </p>
        </div>
      </div>
    </footer>
  );
};
